import { Controller } from "@hotwired/stimulus"
import { checkStorageHealth, estimateStorage } from "../field_storage_health"
import { logWarn, logInfo, getPlatformSnapshot } from "../field_logger"
import { storageUsage } from "../field_image_queue"
import { queueStorageReading } from "../field_settings_sync"

// Connects to data-controller="field-storage-health" (the field layout body).
//
// Non-blocking storage warning (card #3995). Checks that the phone can store
// and read back a small test file and that it has room left, at app start and
// again when the camera opens. When it cannot, the server-rendered banner is
// revealed ("Keep this page open until the upload bar clears") and a
// "Storage health degraded" line is logged. It never blocks capture: the check
// runs beside the camera opening, nothing waits for it, and any failure here
// is swallowed.
//
// Card #4088 (support card #4087) -- a bare probe timeout no longer warns on
// its own: checkStorageHealth retries once internally, and the banner only
// shows for a real write failure, read-back mismatch, or low quota. A timeout
// is still logged ("Storage probe timeout") every time, whether or not the
// retry recovered, so its rate stays visible without alarming a tasker whose
// phone is actually fine.
//
// Card #4215 -- this same controller also takes and logs a plain "Storage
// reading" (estimateStorage() + the photo queue size, no probe) at app open,
// shift start and a vault failure (field:vault-failed, dispatched by
// field_photo_vault.js and field_image_queue.js), so head office can see
// whether storage explains a lost photo on the Field Signal page. Record and
// show only -- this never changes capture behaviour. Open and shift start are
// throttled to one reading every STORAGE_READING_INTERVAL_MS per device; a
// vault failure is always read, so the failure and the reading it causes
// always land together.
//
// Targets:
//   banner -- the hidden notice (field/shared/_storage_health_banner)

// Shared across page loads (the body is replaced on every Turbo visit, and so
// is this controller): one probe in flight at a time, no more than one every
// CHECK_MIN_INTERVAL_MS, and the same warning logged at most once per
// LOG_MIN_INTERVAL_MS while it stays the same.
const CHECK_MIN_INTERVAL_MS = 10_000
const LOG_MIN_INTERVAL_MS   = 10 * 60 * 1000

export const STORAGE_READING_INTERVAL_MS = 30 * 60 * 1000
export const STORAGE_READING_LAST_KEY = "field:storage_reading_last_at"

let inFlight = null
let lastCheckAt = 0
let lastResult = null
let lastLogged = { key: null, at: 0 }
let lastTimeoutLoggedAt = 0
let dismissed = false

export default class extends Controller {
  static targets = ["banner"]

  connect() {
    // A previous page already found the phone struggling: show it at once
    // rather than after the next probe.
    this._render(lastResult)

    this._cameraOpenHandler = () => this._check("before_capture")
    window.addEventListener("field:photo-camera:open", this._cameraOpenHandler)

    this._vaultFailedHandler = () => this._recordStorageReading("vault_failed")
    window.addEventListener("field:vault-failed", this._vaultFailedHandler)

    this._check("app_start")
    this._recordStorageReading("open")
  }

  disconnect() {
    window.removeEventListener("field:photo-camera:open", this._cameraOpenHandler)
    window.removeEventListener("field:vault-failed", this._vaultFailedHandler)
  }

  // Stimulus action target: data-action="submit->field-storage-health#logShiftStart"
  // on the start-shift form (field/shifts/index.html.erb), alongside
  // field-battery's own logShiftStart.
  logShiftStart() {
    this._recordStorageReading("shift_start")
  }

  dismiss() {
    dismissed = true
    this._render(null)
  }

  // -- private --

  // Fire and forget. Callers never await this, and it never throws.
  _check(trigger) {
    this._runCheck(trigger).catch(() => {})
  }

  async _runCheck(trigger) {
    const result = await this._sharedCheck()
    if (!result) return

    // Card #4088 -- logged whether or not the retry inside checkStorageHealth
    // recovered, so the timeout's rate stays visible even when it never
    // becomes a warning.
    if (result.probeTimedOut) this._logProbeTimeout(trigger, result)
    if (result.degraded) this._logDegraded(trigger, result)
    // The element may have gone with the page while the probe ran.
    if (this.element.isConnected) this._render(result)
  }

  async _sharedCheck() {
    if (inFlight) return inFlight
    if (lastResult && Date.now() - lastCheckAt < CHECK_MIN_INTERVAL_MS) return lastResult

    inFlight = checkStorageHealth()
      .then(result => {
        lastResult = result
        lastCheckAt = Date.now()
        return result
      })
      .finally(() => { inFlight = null })
    return inFlight
  }

  _logDegraded(trigger, result) {
    const key = result.reasons.join(",") + "|" + (result.errorName || "")
    const now = Date.now()
    if (lastLogged.key === key && now - lastLogged.at < LOG_MIN_INTERVAL_MS) return
    lastLogged = { key, at: now }

    const platform = getPlatformSnapshot()
    logWarn("storage", "Storage health degraded", {
      trigger,
      platform:    platform.os,
      displayMode: platform.displayMode,
      reasons:     result.reasons,
      quotaBytes:  result.quotaBytes,
      usageBytes:  result.usageBytes,
      freeBytes:   result.freeBytes,
      usedPercent: result.usedPercent,
      errorName:   result.errorName
    }).catch(() => {})
  }

  // Card #4088 -- a probe timeout alone no longer warns the tasker (retried
  // once inside checkStorageHealth), but it is still logged at the same
  // throttle as a real warning so the timeout rate stays visible to head
  // office, recovered or not.
  _logProbeTimeout(trigger, result) {
    const now = Date.now()
    if (now - lastTimeoutLoggedAt < LOG_MIN_INTERVAL_MS) return
    lastTimeoutLoggedAt = now

    const platform = getPlatformSnapshot()
    logInfo("storage", "Storage probe timeout", {
      trigger,
      platform:    platform.os,
      recovered:   result.probeOk,
      quotaBytes:  result.quotaBytes,
      usageBytes:  result.usageBytes,
      freeBytes:   result.freeBytes,
      usedPercent: result.usedPercent
    }).catch(() => {})
  }

  _render(result) {
    if (!this.hasBannerTarget) return
    const show = !!result?.degraded && !dismissed
    this.bannerTarget.classList.toggle("hidden", !show)
    // Card #4308: the server also renders a plain `hidden` attribute as a
    // no-CSS guard. Setting the IDL property keeps the attribute in sync so
    // the guard never outlives the probe that's supposed to clear it.
    this.bannerTarget.hidden = !show
  }

  // Card #4215 -- fire and forget, like _check above. Open and shift_start
  // are gated by STORAGE_READING_INTERVAL_MS; vault_failed always goes
  // through, so a failure's as-at reading is never skipped for being "too
  // soon". The reading itself is estimateStorage() (no probe) plus the
  // photo queue size (storageUsage(), already computed for the Sync page);
  // logging it is unconditional whenever a reading is taken, and
  // queueStorageReading separately decides whether it is worth a network
  // write.
  _recordStorageReading(trigger) {
    this._runStorageReading(trigger).catch(() => {})
  }

  async _runStorageReading(trigger) {
    if (trigger !== "vault_failed") {
      if (!this._storageReadingDue()) return
      this._storeStorageReadingAt(Date.now())
    }

    const [estimate, usage] = await Promise.all([
      estimateStorage().catch(() => null),
      storageUsage().catch(() => null)
    ])
    if (!estimate && !usage) return

    const reading = {
      usageMb:     estimate?.usageBytes != null ? _bytesToMb(estimate.usageBytes) : null,
      quotaMb:     estimate?.quotaBytes != null ? _bytesToMb(estimate.quotaBytes) : null,
      percentUsed: estimate?.usedPercent ?? null,
      persisted:   estimate?.persisted ?? null,
      queueMb:     usage ? Number(usage.pendingMB) : null,
      trigger
    }

    logInfo("storage", "Storage reading", reading).catch(() => {})
    queueStorageReading(reading, { force: trigger === "vault_failed" }).catch(() => {})
  }

  _storageReadingDue() {
    const last = Number(this._readLocal(STORAGE_READING_LAST_KEY))
    return !(last > 0) || Date.now() - last >= STORAGE_READING_INTERVAL_MS
  }

  _storeStorageReadingAt(timestamp) {
    try {
      localStorage.setItem(STORAGE_READING_LAST_KEY, String(timestamp))
    } catch {
      // storage blocked: worst case is an extra reading next trigger
    }
  }

  _readLocal(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }
}

function _bytesToMb(bytes) {
  return Math.round((bytes / 1024 / 1024) * 10) / 10
}
